import { ref, computed, onMounted } from 'vue'

const hideMenuImages = ref(false)
const compactMode = ref(false)
const fontFamily = ref('system-ui, -apple-system, sans-serif')
const fontWeight = ref('400')
const baseFontSize = ref(14)
const navStyle = ref('vertical-tabs')
const categoryPlacement = ref('horizontal')
const cartAnimation = ref(true)
const isLoaded = ref(false)

export const usePosDisplaySettings = () => {
  const { getPosSetting, savePosSetting } = usePosDatabase()

  const loadSettings = async () => {
    if (isLoaded.value) return

    try {
      const [hideImages, compact, font, weight, fontSize, nav, catPlacement, cartAnim] = await Promise.all([
        getPosSetting('pos_hide_menu_images'),
        getPosSetting('pos_compact_mode'),
        getPosSetting('pos_font_family'),
        getPosSetting('pos_font_weight'),
        getPosSetting('pos_base_font_size'),
        getPosSetting('pos_nav_style'),
        getPosSetting('pos_category_placement'),
        getPosSetting('pos_cart_animation')
      ])

      hideMenuImages.value = hideImages === '1'
      compactMode.value = compact === '1'
      if (font) fontFamily.value = font
      if (weight) fontWeight.value = weight
      if (fontSize) baseFontSize.value = parseInt(fontSize)
      if (nav) navStyle.value = nav
      if (catPlacement) categoryPlacement.value = catPlacement
      cartAnimation.value = cartAnim !== '0'

      isLoaded.value = true
    } catch (err) {
      console.error('[PosDisplaySettings] Failed to load settings:', err)
    }
  }

  const refreshSettings = async () => {
    isLoaded.value = false
    await loadSettings()
  }

  const updateSettings = async (settings) => {
    if (settings.hideMenuImages !== undefined) {
      hideMenuImages.value = settings.hideMenuImages
      await savePosSetting('pos_hide_menu_images', settings.hideMenuImages ? '1' : '0')
    }
    if (settings.compactMode !== undefined) {
      compactMode.value = settings.compactMode
      await savePosSetting('pos_compact_mode', settings.compactMode ? '1' : '0')
    }
    if (settings.fontFamily !== undefined) {
      fontFamily.value = settings.fontFamily
      await savePosSetting('pos_font_family', settings.fontFamily)
    }
    if (settings.fontWeight !== undefined) {
      fontWeight.value = settings.fontWeight
      await savePosSetting('pos_font_weight', settings.fontWeight)
    }
    if (settings.baseFontSize !== undefined) {
      baseFontSize.value = settings.baseFontSize
      await savePosSetting('pos_base_font_size', String(settings.baseFontSize))
    }
    if (settings.navStyle !== undefined) {
      navStyle.value = settings.navStyle
      await savePosSetting('pos_nav_style', settings.navStyle)
    }
    if (settings.categoryPlacement !== undefined) {
      categoryPlacement.value = settings.categoryPlacement
      await savePosSetting('pos_category_placement', settings.categoryPlacement)
    }
    if (settings.cartAnimation !== undefined) {
      cartAnimation.value = settings.cartAnimation
      await savePosSetting('pos_cart_animation', settings.cartAnimation ? '1' : '0')
    }
  }

  const isCompactScreen = computed(() => {
    if (typeof window === 'undefined') return false
    return window.innerHeight <= 800 || compactMode.value
  })

  const cssVariables = computed(() => ({
    '--pos-font-family': fontFamily.value,
    '--pos-font-weight': fontWeight.value,
    '--pos-font-size': `${baseFontSize.value}px`,
    '--pos-font-size-sm': `${baseFontSize.value - 2}px`,
    '--pos-font-size-xs': `${baseFontSize.value - 4}px`,
    '--pos-font-size-lg': `${baseFontSize.value + 2}px`
  }))

  return {
    hideMenuImages,
    compactMode,
    fontFamily,
    fontWeight,
    baseFontSize,
    navStyle,
    categoryPlacement,
    cartAnimation,
    isCompactScreen,
    cssVariables,
    loadSettings,
    refreshSettings,
    updateSettings
  }
}
